import React, { useState, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Splat } from '@react-three/drei';
import * as THREE from 'three';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase/config';
import WaypointSpawner from '../WaypointSpawner';
import { testConnection, disconnect, onConnectionChange, getConnectionState } from '../api/config';
import { sendBatchWaypoints } from '../api/waypoint';
import ElevationSettings from './ElevationSettings';
import POIMarkers from './POIMarkers';
import { POIS } from '../constants/pois';

// Ground Elevation Configuration
// Location: 43°28'15.7"N 80°32'26.4"W (Kitchener-Waterloo, Ontario)
// Field-tested value in WGS84 Ellipsoid coordinates
const DEFAULT_GROUND_ELEVATION = 295; // meters

// Initial Camera Configuration
const INITIAL_CAMERA_POSITION = {
    x: -0.195,
    y: -1.312,
    z: -5.641
};

const INITIAL_CAMERA_QUATERNION = {
    x: -0.9885,
    y: 0.0035,
    z: 0.0331,
    w: 0.1472
};

// Keyboard status display component
function KeyboardDisplay({ keysPressed }) {
    // Filter only camera control keys
    const cameraKeys = ['KeyQ', 'KeyE', 'KeyA', 'KeyD', 'KeyW', 'KeyS', 'KeyI', 'KeyK', 'KeyJ', 'KeyL', 'KeyU', 'KeyO', 'KeyR'];
    const pressedCameraKeys = Object.keys(keysPressed).filter(key =>
        keysPressed[key] && cameraKeys.includes(key)
    );

    const keyLabels = {
        'KeyQ': 'Q (Down)',
        'KeyE': 'E (Up)',
        'KeyA': 'A (Left)',
        'KeyD': 'D (Right)',
        'KeyW': 'W (Forward)',
        'KeyS': 'S (Backward)',
        'KeyI': 'I (Pitch Up)',
        'KeyK': 'K (Pitch Down)',
        'KeyJ': 'J (Yaw Left)',
        'KeyL': 'L (Yaw Right)',
        'KeyU': 'U (Roll Left)',
        'KeyO': 'O (Roll Right)',
        'KeyR': 'R (Reset)'
    };

    return (
        <div style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            zIndex: 1000,
            background: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            padding: '10px',
            borderRadius: '5px',
            fontFamily: 'monospace',
            fontSize: '12px',
            minWidth: '200px'
        }}>
            <div style={{ marginBottom: '5px', fontWeight: 'bold' }}>
                Camera Controls:
            </div>
            {pressedCameraKeys.length > 0 ? (
                pressedCameraKeys.map(key => (
                    <div key={key} style={{
                        color: '#00ff00',
                        fontWeight: 'bold',
                        marginBottom: '2px'
                    }}>
                        ● {keyLabels[key]}
                    </div>
                ))
            ) : (
                <div style={{ color: '#888' }}>No keys pressed</div>
            )}
            <div style={{ marginTop: '10px', fontSize: '10px', color: '#aaa' }}>
                Move: WASD+QE | Rotate: IJKLOP | Reset: R
            </div>
        </div>
    );
}

// Keyboard control component
function KeyboardControls({ onKeysChange }) {
    const { camera } = useThree();
    const [keysPressed, setKeysPressed] = useState({});

    useEffect(() => {
        const handleKeyDown = (event) => {
            // Exclude keys used by WaypointSpawner and POI toggle
            // Space is used for POI toggle, so exclude it
            if (!['Equal', 'Exclamation'].includes(event.code) && event.key !== '+' && event.key !== '!') {
                setKeysPressed((prevKeys) => {
                    const newKeys = { ...prevKeys, [event.code]: true };
                    onKeysChange(newKeys); // Pass key state to parent component
                    return newKeys;
                });
            }
        };

        const handleKeyUp = (event) => {
            if (!['Equal', 'Exclamation'].includes(event.code) && event.key !== '+' && event.key !== '!') {
                setKeysPressed((prevKeys) => {
                    const newKeys = { ...prevKeys, [event.code]: false };
                    onKeysChange(newKeys); // Pass key state to parent component
                    return newKeys;
                });
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, [onKeysChange]);

    useFrame((state, delta) => {
        if (!keysPressed) return;

        const moveSpeed = delta * 5;
        const rotateSpeed = delta * 3;

        // Camera movement based on local coordinate system (original method)
        if (keysPressed['KeyQ']) camera.translateY(-moveSpeed);
        if (keysPressed['KeyE']) camera.translateY(moveSpeed);
        if (keysPressed['KeyA']) camera.translateX(-moveSpeed);
        if (keysPressed['KeyD']) camera.translateX(moveSpeed);
        if (keysPressed['KeyW']) camera.translateZ(-moveSpeed);
        if (keysPressed['KeyS']) camera.translateZ(moveSpeed);

        // Precise rotation (original method)
        const effectiveRotationDelta = rotateSpeed * 0.1;

        // Calculate local axes
        const localXAxis = new THREE.Vector3(1, 0, 0)
            .applyQuaternion(camera.quaternion)
            .normalize();
        const localYAxis = new THREE.Vector3(0, 1, 0)
            .applyQuaternion(camera.quaternion)
            .normalize();
        const localZAxis = new THREE.Vector3(0, 0, 1)
            .applyQuaternion(camera.quaternion)
            .normalize();

        // Pitch (up/down)
        if (keysPressed['KeyI']) {
            const pitchQuaternion = new THREE.Quaternion().setFromAxisAngle(
                localXAxis, effectiveRotationDelta
            );
            camera.quaternion.multiplyQuaternions(pitchQuaternion, camera.quaternion);
        }
        if (keysPressed['KeyK']) {
            const pitchQuaternion = new THREE.Quaternion().setFromAxisAngle(
                localXAxis, -effectiveRotationDelta
            );
            camera.quaternion.multiplyQuaternions(pitchQuaternion, camera.quaternion);
        }

        // Yaw (left/right)
        if (keysPressed['KeyJ']) {
            const yawQuaternion = new THREE.Quaternion().setFromAxisAngle(
                localYAxis, effectiveRotationDelta
            );
            camera.quaternion.multiplyQuaternions(yawQuaternion, camera.quaternion);
        }
        if (keysPressed['KeyL']) {
            const yawQuaternion = new THREE.Quaternion().setFromAxisAngle(
                localYAxis, -effectiveRotationDelta
            );
            camera.quaternion.multiplyQuaternions(yawQuaternion, camera.quaternion);
        }

        // Roll (left/right)
        if (keysPressed['KeyU']) {
            const yawQuaternion = new THREE.Quaternion().setFromAxisAngle(
                localZAxis, effectiveRotationDelta
            );
            camera.quaternion.multiplyQuaternions(yawQuaternion, camera.quaternion);
        }
        if (keysPressed['KeyO']) {
            const yawQuaternion = new THREE.Quaternion().setFromAxisAngle(
                localZAxis, -effectiveRotationDelta
            );
            camera.quaternion.multiplyQuaternions(yawQuaternion, camera.quaternion);
        }

        camera.quaternion.normalize();

        // Camera reset - use initial configuration
        if (keysPressed['KeyR']) {
            camera.position.set(
                INITIAL_CAMERA_POSITION.x,
                INITIAL_CAMERA_POSITION.y,
                INITIAL_CAMERA_POSITION.z
            );
            camera.quaternion.set(
                INITIAL_CAMERA_QUATERNION.x,
                INITIAL_CAMERA_QUATERNION.y,
                INITIAL_CAMERA_QUATERNION.z,
                INITIAL_CAMERA_QUATERNION.w
            );
        }
    });

    return null;
}

function Scene({ onKeysChange, user, onCameraUpdate, gndElevation, showPOIs, onPOIClick, onPOIsUpdate }) {
    const { camera } = useThree();

    useEffect(() => {
        // Initial camera position - use configuration constant
        const position = new THREE.Vector3(
            INITIAL_CAMERA_POSITION.x,
            INITIAL_CAMERA_POSITION.y,
            INITIAL_CAMERA_POSITION.z
        );

        camera.position.copy(position);

        // Initial camera quaternion - use configuration constant
        camera.quaternion.set(
            INITIAL_CAMERA_QUATERNION.x,
            INITIAL_CAMERA_QUATERNION.y,
            INITIAL_CAMERA_QUATERNION.z,
            INITIAL_CAMERA_QUATERNION.w
        );

        camera.updateProjectionMatrix();

    }, [camera]);

    // Update camera info to parent component
    useEffect(() => {
        const interval = setInterval(() => {
            if (onCameraUpdate) {
                onCameraUpdate({
                    position: camera.position,
                    quaternion: camera.quaternion
                });
            }
        }, 100); // Update every 100ms

        return () => clearInterval(interval);
    }, [camera, onCameraUpdate]);

    return (
        <>
            <ambientLight intensity={Math.PI / 2}/>
            <Splat src="model/loading_dock.splat"/>
            <OrbitControls
                enabled={false}  // Completely disable OrbitControls
            />
            <WaypointSpawner sceneCamera={camera} user={user} gndElevation={gndElevation}/>
            <KeyboardControls onKeysChange={onKeysChange} />
            <POIMarkers
                pois={[]}
                visible={showPOIs}
                onPOIClick={onPOIClick}
                onPOIsUpdate={onPOIsUpdate}
                sceneCamera={camera}
            />
        </>
    );
}

function Viewer3D({ user }) {
  const [currentKeys, setCurrentKeys] = useState({});
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('');
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [apiFeedback, setApiFeedback] = useState({ message: '', type: '', timestamp: null });
  const [cameraInfo, setCameraInfo] = useState({ position: null, quaternion: null });
  const [gndElevation, setGndElevation] = useState(DEFAULT_GROUND_ELEVATION);
  const [showPOIs, setShowPOIs] = useState(true);  // POI marker visibility (default: visible)
  const [allPOIs, setAllPOIs] = useState([]);  // All POI information received from POIMarkers

  useEffect(() => {
    // Listen to connection state changes
    const unsubscribe = onConnectionChange((connected) => {
      setIsConnected(connected);
    });
    return unsubscribe;
  }, []);

  // Expose setApiFeedback to window for WaypointSpawner
  useEffect(() => {
    window.setApiFeedback = (message, type = 'info') => {
      setApiFeedback({ message, type, timestamp: Date.now() });
    };
    return () => {
      delete window.setApiFeedback;
    };
  }, []);

  const handleTestConnection = async () => {
    setIsTestingConnection(true);
    setConnectionStatus('Testing connection...');

    try {
      const token = await user.getIdToken();
      const result = await testConnection(token);

      if (result.success) {
        setConnectionStatus('✓ Backend Connected');
      } else {
        setConnectionStatus(`✗ Connection failed: ${result.error}`);
      }
    } catch (error) {
      setConnectionStatus(`✗ Error: ${error.message}`);
    } finally {
      setIsTestingConnection(false);
    }
  };

  const handleDisconnect = () => {
    disconnect();
    setConnectionStatus('');
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // Toggle POI markers with spacebar
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code === 'Space' && !event.repeat) {
        // Toggle with Space key only (Shift not required)
        event.preventDefault();
        setShowPOIs(prev => {
          const newState = !prev;
          console.log(`POI markers ${newState ? 'shown' : 'hidden'}`);
          if (window.setApiFeedback) {
            window.setApiFeedback(
              `POI markers ${newState ? '✓ shown' : '✗ hidden'}`,
              'info'
            );
          }
          return newState;
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // POI update handler - receives all POI information from POIMarkers
  const handlePOIsUpdate = (pois) => {
    setAllPOIs(pois);
  };

  // Send all POIs as waypoints with * key
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === '*' && event.shiftKey) {  // '*' is Shift + 8
        event.preventDefault();

        // Cannot send if POI markers are hidden
        if (!showPOIs) {
          if (window.setApiFeedback) {
            window.setApiFeedback('❌ POI markers are hidden\nPress SPACE to show markers first', 'error');
          }
          return;
        }

        // Check backend connection
        if (!getConnectionState()) {
          if (window.setApiFeedback) {
            window.setApiFeedback('❌ Backend not connected\nPlease test connection first', 'error');
          }
          return;
        }

        // Return if no POIs
        if (allPOIs.length === 0) {
          if (window.setApiFeedback) {
            window.setApiFeedback('❌ No POI markers to send', 'error');
          }
          return;
        }

        // Convert all POIs to waypoint data
        const waypointDataArray = allPOIs.map(poi => ({
          position: {
            x: Number(poi.position.x.toFixed(5)),
            y: Number(poi.position.y.toFixed(5)),
            z: Number(poi.position.z.toFixed(5))
          },
          rotation: {
            x: Number(poi.rotation.x.toFixed(5)),
            y: Number(poi.rotation.y.toFixed(5)),
            z: Number(poi.rotation.z.toFixed(5)),
            w: Number(poi.rotation.w.toFixed(5))
          }
        }));

        // Send feedback
        if (window.setApiFeedback) {
          window.setApiFeedback(`📤 Sending ${allPOIs.length} POI waypoints...`, 'info');
        }

        // Send batch to backend
        sendBatchWaypoints(user, waypointDataArray, gndElevation).then(result => {
          if (result.success) {
            let message = `✅ ${allPOIs.length} waypoints sent successfully!\n\n`;
            message += `Batch ID: ${result.data.batchId}\n`;
            message += `GND Elevation: ${gndElevation}m\n\n`;
            message += `POIs: ${allPOIs.map(p => p.name).join(', ')}\n\n`;

            // Add published information from backend response
            if (result.data.published) {
              message += `Published to:\n`;
              if (result.data.published.gps) message += `  • GPS: ${result.data.published.gps} waypoints\n`;
              if (result.data.published.utm) message += `  • UTM: ${result.data.published.utm} waypoints\n`;
              if (result.data.published.colmap) message += `  • COLMAP: ${result.data.published.colmap} waypoints`;
            }

            if (window.setApiFeedback) {
              window.setApiFeedback(message, 'success');
            }
          } else {
            if (window.setApiFeedback) {
              window.setApiFeedback(`❌ Failed to send waypoints\n\n${result.error}`, 'error');
            }
          }
        }).catch(error => {
          if (window.setApiFeedback) {
            window.setApiFeedback(`❌ Network error\n\n${error.message}`, 'error');
          }
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [allPOIs, user, gndElevation, showPOIs]);

  // POI click handler - Create and send waypoint
  const handlePOIClick = (poi) => {
    console.log('POI clicked:', poi);

    // Check backend connection
    if (!getConnectionState()) {
      if (window.setApiFeedback) {
        window.setApiFeedback('❌ Backend not connected\nPlease test connection first', 'error');
      }
      return;
    }

    // Create waypoint data
    const waypointData = {
      position: {
        x: Number(poi.position.x.toFixed(5)),
        y: Number(poi.position.y.toFixed(5)),
        z: Number(poi.position.z.toFixed(5))
      },
      rotation: {
        x: Number(poi.rotation.x.toFixed(5)),
        y: Number(poi.rotation.y.toFixed(5)),
        z: Number(poi.rotation.z.toFixed(5)),
        w: Number(poi.rotation.w.toFixed(5))
      }
    };

    // Send feedback
    if (window.setApiFeedback) {
      window.setApiFeedback(`📤 Sending waypoint: ${poi.name}...`, 'info');
    }

    // Send to backend
    sendBatchWaypoints(user, [waypointData], gndElevation).then(result => {
      if (result.success) {
        let message = `✅ 1 waypoint sent successfully!\n\n`;
        message += `Batch ID: ${result.data.batchId}\n`;
        message += `GND Elevation: ${gndElevation}m\n\n`;

        // Add published information from backend response
        if (result.data.published) {
          message += `Published to:\n`;
          if (result.data.published.gps) message += `  • GPS: ${result.data.published.gps} waypoints\n`;
          if (result.data.published.utm) message += `  • UTM: ${result.data.published.utm} waypoints\n`;
          if (result.data.published.colmap) message += `  • COLMAP: ${result.data.published.colmap} waypoints`;
        }

        if (window.setApiFeedback) {
          window.setApiFeedback(message, 'success');
        }
      } else {
        if (window.setApiFeedback) {
          window.setApiFeedback(`❌ Failed to send waypoint\n\n${result.error}`, 'error');
        }
      }
    }).catch(error => {
      if (window.setApiFeedback) {
        window.setApiFeedback(`❌ Network error\n\n${error.message}`, 'error');
      }
    });
  };

  return (
    <div style={{ position: 'relative', width: '100vw', height: '100vh' }}>
      {/* 로그아웃 버튼 - 우측 상단 */}
      <div style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        zIndex: 1000,
        background: 'rgba(0, 0, 0, 0.8)',
        color: 'white',
        padding: '10px 15px',
        borderRadius: '6px',
        fontFamily: 'monospace',
        fontSize: '12px'
      }}>
        <div style={{ marginBottom: '8px', color: '#0f0', fontSize: '11px' }}>
          ✓ {user.email}
        </div>
        <button
          onClick={handleLogout}
          style={{
            width: '100%',
            padding: '6px 12px',
            background: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            fontFamily: 'monospace'
          }}
        >
          Logout
        </button>
      </div>

      {/* Camera controls - top left */}
      <KeyboardDisplay keysPressed={currentKeys} />

      {/* Camera position/pose info - below camera controls */}
      {cameraInfo.position && cameraInfo.quaternion && (
        <div style={{
          position: 'absolute',
          top: '115px',
          left: '10px',
          zIndex: 1000,
          background: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          padding: '10px',
          borderRadius: '5px',
          fontFamily: 'monospace',
          fontSize: '10px',
          minWidth: '200px'
        }}>
          <div style={{ marginBottom: '5px', fontWeight: 'bold', color: '#667eea' }}>
            Camera Info:
          </div>
          <div style={{ marginBottom: '8px' }}>
            <div style={{ color: '#aaa', fontSize: '9px', marginBottom: '2px' }}>Position:</div>
            <div style={{ color: '#4caf50' }}>
              X: {cameraInfo.position.x.toFixed(3)}<br/>
              Y: {cameraInfo.position.y.toFixed(3)}<br/>
              Z: {cameraInfo.position.z.toFixed(3)}
            </div>
          </div>
          <div>
            <div style={{ color: '#aaa', fontSize: '9px', marginBottom: '2px' }}>Quaternion (XYZW):</div>
            <div style={{ color: '#64b5f6' }}>
              X: {cameraInfo.quaternion.x.toFixed(4)}<br/>
              Y: {cameraInfo.quaternion.y.toFixed(4)}<br/>
              Z: {cameraInfo.quaternion.z.toFixed(4)}<br/>
              W: {cameraInfo.quaternion.w.toFixed(4)}
            </div>
          </div>
        </div>
      )}

      {/* POI/Waypoint operation guide - below camera info */}
      <div style={{
        position: 'absolute',
        top: '315px',
        left: '10px',
        zIndex: 1000,
        background: 'rgba(0, 0, 0, 0.7)',
        color: 'white',
        padding: '10px',
        borderRadius: '5px',
        fontFamily: 'monospace',
        fontSize: '10px',
        minWidth: '200px'
      }}>
        <div style={{ marginBottom: '5px', fontWeight: 'bold', color: '#ffa726' }}>
          Waypoint & POI:
        </div>
        <div style={{ color: '#aaa', lineHeight: '1.6' }}>
          Add Waypoint: + key<br/>
          Send Waypoints: ! key<br/>
          Add POI: Shift+/ key<br/>
          Toggle POI: Space key<br/>
          Send POI: Shift+8 (* key)<br/>
          Click POI: Copy info
        </div>
      </div>

      {/* Backend connection status + API feedback - below waypoint guide */}
      <div style={{
        position: 'absolute',
        top: '480px',
        left: '10px',
        zIndex: 1000,
        background: 'rgba(0, 0, 0, 0.8)',
        color: 'white',
        padding: '10px 15px',
        borderRadius: '6px',
        fontFamily: 'monospace',
        fontSize: '12px',
        minWidth: '220px'
      }}>
        {/* 연결 버튼 */}
        <div style={{ marginBottom: '10px' }}>
          <div style={{ marginBottom: '5px', fontSize: '11px', color: '#aaa' }}>Backend Status:</div>
          {!isConnected ? (
            <button
              onClick={handleTestConnection}
              disabled={isTestingConnection}
              style={{
                width: '100%',
                padding: '6px 12px',
                background: isTestingConnection ? '#666' : '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isTestingConnection ? 'not-allowed' : 'pointer',
                fontSize: '11px',
                fontFamily: 'monospace',
                fontWeight: 'bold'
              }}
            >
              {isTestingConnection ? 'Testing...' : 'Test Connection'}
            </button>
          ) : (
            <button
              onClick={handleDisconnect}
              style={{
                width: '100%',
                padding: '6px 12px',
                background: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '11px',
                fontFamily: 'monospace',
                fontWeight: 'bold'
              }}
            >
              ✓ Connected
            </button>
          )}
          {connectionStatus && (
            <div style={{
              marginTop: '5px',
              fontSize: '9px',
              color: isConnected ? '#4caf50' : '#ff6b6b'
            }}>
              {connectionStatus}
            </div>
          )}
        </div>

        {/* API feedback */}
        {apiFeedback.message && (
          <div style={{
            borderTop: '1px solid #444',
            paddingTop: '10px',
            marginTop: '5px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '5px'
            }}>
              <div style={{ fontSize: '11px', color: '#aaa' }}>API Feedback:</div>
              <button
                onClick={() => setApiFeedback({ message: '', type: '', timestamp: null })}
                style={{
                  padding: '2px 6px',
                  background: '#555',
                  color: '#ccc',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  fontSize: '9px',
                  fontFamily: 'monospace'
                }}
                title="Clear feedback"
              >
                ✕ Clear
              </button>
            </div>
            <div style={{
              padding: '8px',
              borderRadius: '4px',
              background: apiFeedback.type === 'success' ? 'rgba(76, 175, 80, 0.2)' :
                          apiFeedback.type === 'error' ? 'rgba(244, 67, 54, 0.2)' :
                          'rgba(100, 100, 100, 0.2)',
              border: `1px solid ${
                apiFeedback.type === 'success' ? '#4caf50' :
                apiFeedback.type === 'error' ? '#f44336' :
                '#666'
              }`,
              fontSize: '10px',
              lineHeight: '1.4',
              maxHeight: '150px',
              overflowY: 'auto',
              wordBreak: 'break-word'
            }}>
              <div style={{
                color: apiFeedback.type === 'success' ? '#4caf50' :
                       apiFeedback.type === 'error' ? '#ff6b6b' :
                       '#ccc',
                marginBottom: '5px',
                fontWeight: 'bold'
              }}>
                {apiFeedback.type === 'success' ? '✓' : apiFeedback.type === 'error' ? '✗' : '•'}
                {apiFeedback.type === 'success' ? ' Success' : apiFeedback.type === 'error' ? ' Error' : ' Info'}
              </div>
              <pre style={{
                margin: 0,
                fontFamily: 'monospace',
                fontSize: '9px',
                whiteSpace: 'pre-wrap',
                color: '#ddd'
              }}>
                {apiFeedback.message}
              </pre>
            </div>
          </div>
        )}
      </div>

      {/* Ground Elevation Settings - bottom right */}
      <div style={{
        position: 'absolute',
        bottom: '10px',
        right: '10px',
        zIndex: 1000
      }}>
        <ElevationSettings
          currentElevation={gndElevation}
          onElevationChange={setGndElevation}
        />
      </div>

      {/* POI status display - bottom left */}
      <div style={{
        position: 'absolute',
        bottom: '10px',
        left: '10px',
        zIndex: 1000,
        background: 'rgba(0, 0, 0, 0.8)',
        color: 'white',
        padding: '10px 15px',
        borderRadius: '6px',
        fontFamily: 'monospace',
        fontSize: '11px'
      }}>
        <div style={{ marginBottom: '5px', color: '#aaa' }}>POI Markers:</div>
        <div style={{
          color: showPOIs ? '#4caf50' : '#ff6b6b',
          fontWeight: 'bold'
        }}>
          {showPOIs ? '● Visible' : '○ Hidden'}
        </div>
        <div style={{ marginTop: '5px', fontSize: '9px', color: '#aaa' }}>
          Press SPACE to toggle
        </div>
        {showPOIs && (
          <div style={{ marginTop: '5px', fontSize: '9px', color: '#aaa' }}>
            Click marker to send waypoint
          </div>
        )}
      </div>

      <Canvas style={{ width: '100%', height: '100%' }}>
        <Scene
          onKeysChange={setCurrentKeys}
          user={user}
          onCameraUpdate={setCameraInfo}
          gndElevation={gndElevation}
          showPOIs={showPOIs}
          onPOIClick={handlePOIClick}
          onPOIsUpdate={handlePOIsUpdate}
        />
      </Canvas>
    </div>
  );
}

export default Viewer3D;
